import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu, X, ChevronDown, TrendingUp, Clock, XCircle,
  GitCompare,
  FileText, ScrollText, Gavel,
  Filter, MapPin, Building2,
  MessageSquare, CheckCircle, Timer,
  Archive, Target, Sparkles, CalendarDays, Users, Landmark,
  HelpCircle, Mail, Star, ArrowRight
} from 'lucide-react';
import styled from 'styled-components';

// ── Wrapper ────────────────────────────────────────────────────────────────────

const HeaderWrapper = styled.header`
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 1000;
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
  backdrop-filter: blur(10px);
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
`;

// ── Logo ───────────────────────────────────────────────────────────────────────

const Logo = styled(Link)`
  font-size: 1.4rem;
  font-weight: 700;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  flex-shrink: 0;

  .logo-text {
    display: flex;
    flex-direction: column;
    line-height: 1;
    gap: 3px;
  }

  .logo-tagline {
    font-size: 0.5rem;
    font-weight: 400;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    opacity: 0.7;
  }

  &:hover { transform: scale(1.03); }

  .logo-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    transition: all 0.3s ease;
    flex-shrink: 0;
  }

  &:hover .logo-icon {
    transform: rotate(5deg);
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }
`;

// ── Desktop nav ────────────────────────────────────────────────────────────────

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  @media (max-width: 1024px) { display: none; }
`;

const NavItem = styled.div`
  position: relative;

  &:hover .mega-menu {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }

  /* Right-aligned menu (last NavItem with mega-menu) */
  &.right-align:hover .mega-menu {
    transform: translateX(0) translateY(0);
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: rgba(255,255,255,0.88);
  font-weight: 500;
  font-size: 0.92rem;
  padding: 7px 14px;
  border-radius: 20px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;

  &.active {
    color: white;
    background: rgba(255,255,255,0.18);
    font-weight: 600;
  }

  &:hover {
    color: white;
    background: rgba(255,255,255,0.13);
  }
`;

const DropdownTrigger = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(255,255,255,0.88);
  font-weight: 500;
  font-size: 0.92rem;
  padding: 7px 14px;
  border-radius: 20px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  font-family: inherit;

  &.active {
    color: white;
    background: rgba(255,255,255,0.18);
    font-weight: 600;
  }

  &:hover {
    color: white;
    background: rgba(255,255,255,0.13);
  }

  .chevron {
    transition: transform 0.2s ease;
    opacity: 0.7;
  }

  ${NavItem}:hover & .chevron {
    transform: rotate(180deg);
    opacity: 1;
  }
`;

// ── Mega menu ──────────────────────────────────────────────────────────────────

const MegaMenu = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%) translateY(-8px);
  background: white;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.06);
  border: 1px solid rgba(0,0,0,0.07);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.22s ease, transform 0.22s ease, visibility 0.22s;
  z-index: 1001;
  overflow: hidden;

  /* right-align variant */
  .right-align & {
    left: auto;
    right: 0;
    transform: translateY(-8px);
  }
  .right-align:hover & {
    transform: translateY(0);
  }
`;

const MegaMenuInner = styled.div`
  display: flex;
`;

const MegaMenuColumn = styled.div`
  padding: 22px 20px;
  min-width: 200px;

  &:not(:last-child) {
    border-right: 1px solid #f1f5f9;
  }
`;

const ColumnTitle = styled.div`
  font-size: 0.72em;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 4px;
`;

const MenuItem = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px;
  color: #334155;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.88rem;
  transition: all 0.15s ease;
  border-radius: 8px;
  margin-bottom: 1px;

  &:hover {
    background: #f1f5f9;
    color: #4f46e5;

    .menu-icon { color: #4f46e5; }
  }

  .menu-icon {
    color: #94a3b8;
    transition: color 0.15s ease;
    margin-top: 1px;
    flex-shrink: 0;
  }

  .menu-content { flex: 1; min-width: 0; }

  .menu-label {
    display: flex;
    align-items: center;
    gap: 6px;
    line-height: 1.3;
    margin-bottom: 1px;
    white-space: nowrap;
  }

  /* Open Petitions is by far the most-used page in this menu. Without any
     emphasis all twelve items read as equally likely, which leaves the
     reader scanning rather than going. */
  &.primary {
    background: #f8fafc;

    .menu-label { font-weight: 700; color: #1e293b; }
    .menu-icon { color: #4f46e5; }

    &:hover { background: #eef2ff; }
  }

  /* The page you are on, marked inside the menu. Without this the dropdown
     gave no clue where you already were, so "Open Petitions" looked
     identical whether you were on it or not. Declared after .primary so the
     current page wins when it is also the highlighted one. */
  &.current {
    background: #eef2ff;

    .menu-label { font-weight: 700; color: #4338ca; }
    .menu-icon { color: #4f46e5; }
    .menu-desc { color: #6366f1; }
  }

  .menu-desc {
    font-size: 0.75em;
    color: #94a3b8;
    font-weight: 400;
    line-height: 1.3;
  }
`;

const MenuItemExternal = styled.a`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px;
  color: #334155;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.88rem;
  transition: all 0.15s ease;
  border-radius: 8px;
  margin-bottom: 1px;

  &:hover {
    background: #f1f5f9;
    color: #4f46e5;

    .menu-icon { color: #4f46e5; }
  }

  .menu-icon {
    color: #94a3b8;
    transition: color 0.15s ease;
    margin-top: 1px;
    flex-shrink: 0;
  }

  .menu-content { flex: 1; min-width: 0; }

  .menu-label {
    display: flex;
    align-items: center;
    gap: 6px;
    line-height: 1.3;
    margin-bottom: 1px;
  }

  .menu-desc {
    font-size: 0.75em;
    color: #94a3b8;
    font-weight: 400;
    line-height: 1.3;
  }
`;

const Badge = styled.span`
  font-size: 0.65em;
  padding: 2px 5px;
  border-radius: 4px;
  font-weight: 700;
  letter-spacing: 0.2px;
  line-height: 1;
  background: ${p => p.hot
    ? 'linear-gradient(135deg, #f97316, #dc2626)'
    : p.live
    ? '#dc2626'
    : 'linear-gradient(135deg, #667eea, #764ba2)'};
  color: white;
`;

const MegaMenuFooter = styled.div`
  background: #f8fafc;
  padding: 12px 20px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FooterLink = styled(Link)`
  color: #4f46e5;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: gap 0.2s ease;

  &:hover { gap: 7px; }
`;

const FooterNote = styled.span`
  color: #94a3b8;
  font-size: 0.8rem;
`;

// ── Mobile ─────────────────────────────────────────────────────────────────────

const MobileMenuButton = styled.button`
  display: none;
  background: rgba(255,255,255,0.15);
  border: none;
  cursor: pointer;
  color: white;
  border-radius: 8px;
  padding: 8px;
  transition: background 0.2s ease;

  &:hover { background: rgba(255,255,255,0.25); }

  @media (max-width: 1024px) { display: flex; align-items: center; }
`;

const MobileNav = styled.nav`
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  background: white;
  display: ${p => p.open ? 'block' : 'none'};
  z-index: 999;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  max-height: calc(100vh - 70px);
  overflow-y: auto;
`;

const MobileSection = styled.div`
  padding: 14px 20px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child { border-bottom: none; }
`;

const MobileSectionTitle = styled.div`
  font-size: 0.72em;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MobileLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 4px;
  color: #334155;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  transition: color 0.15s ease;
  border-radius: 6px;

  &.active { color: #4f46e5; font-weight: 600; }
  &:hover { color: #4f46e5; }

  .icon { color: #94a3b8; flex-shrink: 0; }
`;

const MobileLinkExternal = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 4px;
  color: #334155;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  transition: color 0.15s ease;

  &:hover { color: #4f46e5; }

  .icon { color: #94a3b8; flex-shrink: 0; }
`;

// ── Component ──────────────────────────────────────────────────────────────────

const Header = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [counts] = useState({ awaitingResponse: null, debateScheduled: null });

  useEffect(() => { setMenuOpen(false); }, [location]);

  /* The Petitions trigger highlights for any page inside its menu. Trending
     and Local are excluded because they have their own nav links, which
     light up instead; /compare is included because it is listed under
     Analysis even though its path sits outside /petitions. */
  const isPetitionsActive = (location.pathname.startsWith('/petitions')
    && location.pathname !== '/petitions/local'
    && location.pathname !== '/petitions/trending')
    || location.pathname === '/compare';
  const isInfoActive = ['/faq', '/about', '/contact'].includes(location.pathname);
  /* Marks the menu entry for the page currently open. `primary` keeps the
     Open Petitions emphasis when it is not the current page. */
  const menuClass = (path, primary = false) => {
    const classes = [];
    if (primary) classes.push('primary');
    if (location.pathname === path) classes.push('current');
    return classes.join(' ') || undefined;
  };

  return (
    <HeaderWrapper>
      <Container>

        {/* Logo */}
        <Logo to="/">
          <div className="logo-icon">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <rect width="40" height="40" rx="8" fill="#012169" />
              <path d="M0 0L40 40M40 0L0 40" stroke="white" strokeWidth="6" />
              <path d="M0 0L40 40" stroke="#C8102E" strokeWidth="3" />
              <path d="M40 0L0 40" stroke="#C8102E" strokeWidth="3" />
              <path d="M20 0V40M0 20H40" stroke="white" strokeWidth="10" />
              <path d="M20 0V40M0 20H40" stroke="#C8102E" strokeWidth="6" />
            </svg>
          </div>
          <div className="logo-text">
            <span>UK Petition Tracker</span>
            <span className="logo-tagline" aria-hidden="true">Democracy in Numbers</span>
          </div>
        </Logo>

        {/* Desktop nav */}
        <Nav>

          <NavLink to="/" className={location.pathname === '/' ? 'active' : ''}>
            Home
          </NavLink>

          {/* ── Petitions mega menu ── */}
          <NavItem>
            <DropdownTrigger className={isPetitionsActive ? 'active' : ''}>
              Petitions
              <ChevronDown size={15} className="chevron" />
            </DropdownTrigger>

            <MegaMenu className="mega-menu" style={{ width: 760 }}>
              <MegaMenuInner>

                {/* Col 1 — where a petition is in its life. Recently
                    Opened and Recently Closed sit beside Open and Closed
                    rather than under a separate "Discover" heading, which
                    is where they were and where nobody would look. */}
                <MegaMenuColumn>
                  <ColumnTitle><Filter size={13} /> Status</ColumnTitle>

                  <MenuItem to="/petitions/open" className={menuClass('/petitions/open', true)}>
                    <CheckCircle size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">Open Petitions</div>
                      <div className="menu-desc">Currently accepting signatures</div>
                    </div>
                  </MenuItem>

                  <MenuItem to="/petitions/recently-opened" className={menuClass('/petitions/recently-opened')}>
                    <Sparkles size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">Recently Opened</div>
                      <div className="menu-desc">New petitions this week</div>
                    </div>
                  </MenuItem>

                  <MenuItem to="/petitions/closed" className={menuClass('/petitions/closed')}>
                    <Clock size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">Closed Petitions</div>
                      <div className="menu-desc">No longer accepting signatures</div>
                    </div>
                  </MenuItem>

                  <MenuItem to="/petitions/recently-closed" className={menuClass('/petitions/recently-closed')}>
                    <Archive size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">Recently Closed</div>
                      <div className="menu-desc">Closed in the last 30 days</div>
                    </div>
                  </MenuItem>

                  <MenuItem to="/petitions/rejected" className={menuClass('/petitions/rejected')}>
                    <XCircle size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">Rejected Petitions</div>
                      <div className="menu-desc">Not approved for publication</div>
                    </div>
                  </MenuItem>
                </MegaMenuColumn>

                {/* Col 2 — what Parliament has done, in the order it
                    happens: awaiting a reply, replied, scheduled, debated. */}
                <MegaMenuColumn>
                  <ColumnTitle><Gavel size={13} /> In Parliament</ColumnTitle>

                  <MenuItem to="/petitions/awaiting-response" className={menuClass('/petitions/awaiting-response')}>
                    <Timer size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">
                        Awaiting Response
                        {counts.awaitingResponse ? <Badge>{counts.awaitingResponse}</Badge> : null}
                      </div>
                      <div className="menu-desc">Passed 10,000, reply due</div>
                    </div>
                  </MenuItem>

                  <MenuItem to="/petitions/government-responded" className={menuClass('/petitions/government-responded')}>
                    <MessageSquare size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">Government Responded</div>
                      <div className="menu-desc">Official replies published</div>
                    </div>
                  </MenuItem>

                  <MenuItem to="/petitions/debate-scheduled" className={menuClass('/petitions/debate-scheduled')}>
                    <CalendarDays size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">
                        Debate Scheduled
                        {counts.debateScheduled ? <Badge hot>{counts.debateScheduled}</Badge> : null}
                      </div>
                      <div className="menu-desc">Dates set for a debate</div>
                    </div>
                  </MenuItem>

                  <MenuItem to="/petitions/debated" className={menuClass('/petitions/debated')}>
                    <Landmark size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">Debated</div>
                      <div className="menu-desc">Already discussed in Parliament</div>
                    </div>
                  </MenuItem>
                </MegaMenuColumn>

                {/* Col 3 — the analysis pages, which are the reason to use
                    this site rather than Parliament's own. Compare, Trending
                    and Local are not repeated here: all three already sit in
                    the nav bar one click away. */}
                <MegaMenuColumn>
                  <ColumnTitle><Target size={13} /> Analysis</ColumnTitle>

                  <MenuItem to="/petitions/milestones" className={menuClass('/petitions/milestones')}>
                    <Target size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">Near Milestones</div>
                      <div className="menu-desc">Close to 10,000 or 100,000</div>
                    </div>
                  </MenuItem>

                  <MenuItem to="/compare" className={menuClass('/compare')}>
                    <GitCompare size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">Compare Petitions</div>
                      <div className="menu-desc">Two petitions, one timeline</div>
                    </div>
                  </MenuItem>

                  <MenuItem to="/petitions/local" className={menuClass('/petitions/local')}>
                    <MapPin size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">By Constituency</div>
                      <div className="menu-desc">Support across all 650 seats</div>
                    </div>
                  </MenuItem>
                </MegaMenuColumn>

              </MegaMenuInner>
              <MegaMenuFooter>
                <FooterNote>All UK Parliament &amp; Government petitions</FooterNote>
                <FooterLink to="/petitions/open">
                  Browse All <ArrowRight size={13} />
                </FooterLink>
              </MegaMenuFooter>
            </MegaMenu>
          </NavItem>

          <NavLink to="/compare" className={location.pathname === '/compare' ? 'active' : ''}>
            <GitCompare size={15} />
            Compare
          </NavLink>

          <NavLink to="/petitions/trending" className={location.pathname === '/petitions/trending' ? 'active' : ''}>
            <TrendingUp size={15} />
            Trending
          </NavLink>

          <NavLink to="/petitions/local" className={location.pathname === '/petitions/local' ? 'active' : ''}>
            <MapPin size={15} />
            Local
          </NavLink>

          {/* ── Info mega menu ── */}
          <NavItem className="right-align">
            <DropdownTrigger className={isInfoActive ? 'active' : ''}>
              More
              <ChevronDown size={15} className="chevron" />
            </DropdownTrigger>

            <MegaMenu className="mega-menu" style={{ width: 440, left: 'auto', right: 0, transform: 'translateY(-8px)' }}>
              <MegaMenuInner>

                {/* Col 1 — Help */}
                <MegaMenuColumn style={{ flex: 1 }}>
                  <ColumnTitle><HelpCircle size={13} /> Help &amp; Info</ColumnTitle>

                  <MenuItem to="/faq" className={menuClass('/faq')}>
                    <HelpCircle size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">FAQ</div>
                      <div className="menu-desc">Common questions answered</div>
                    </div>
                  </MenuItem>

                  <MenuItem to="/about" className={menuClass('/about')}>
                    <Users size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">About Us</div>
                      <div className="menu-desc">Our mission &amp; data sources</div>
                    </div>
                  </MenuItem>

                  <MenuItem to="/contact" className={menuClass('/contact')}>
                    <Mail size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">Contact</div>
                      <div className="menu-desc">Get in touch with us</div>
                    </div>
                  </MenuItem>
                </MegaMenuColumn>

                {/* Col 2 — Participate */}
                <MegaMenuColumn style={{ flex: 1 }}>
                  <ColumnTitle><Star size={13} /> Participate</ColumnTitle>

                  <MenuItemExternal
                    href="https://petition.parliament.uk/petitions/new"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ScrollText size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">Create a Petition</div>
                      <div className="menu-desc">Start one on parliament.uk</div>
                    </div>
                  </MenuItemExternal>

                  <MenuItemExternal
                    href="https://petition.parliament.uk"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FileText size={16} className="menu-icon" />
                    <div className="menu-content">
                      <div className="menu-label">Parliament Petitions</div>
                      <div className="menu-desc">Official petition site</div>
                    </div>
                  </MenuItemExternal>
                </MegaMenuColumn>

              </MegaMenuInner>
            </MegaMenu>
          </NavItem>

        </Nav>

        <MobileMenuButton onClick={() => setMenuOpen(p => !p)} aria-label="Toggle menu">
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </MobileMenuButton>
      </Container>

      {/* ── Mobile nav ── */}
      <MobileNav open={menuOpen}>

        <MobileSection>
          <MobileLink to="/" className={location.pathname === '/' ? 'active' : ''}>
            <CheckCircle size={17} className="icon" /> Home
          </MobileLink>
        </MobileSection>

        <MobileSection>
          <MobileSectionTitle><Filter size={13} /> Status</MobileSectionTitle>
          <MobileLink to="/petitions/open" className={location.pathname === '/petitions/open' ? 'active' : ''}>
            <CheckCircle size={17} className="icon" /> Open Petitions
          </MobileLink>
          <MobileLink to="/petitions/awaiting-response" className={location.pathname === '/petitions/awaiting-response' ? 'active' : ''}>
            <Timer size={17} className="icon" /> Awaiting Response
          </MobileLink>
          <MobileLink to="/petitions/closed" className={location.pathname === '/petitions/closed' ? 'active' : ''}>
            <Clock size={17} className="icon" /> Closed Petitions
          </MobileLink>
          <MobileLink to="/petitions/rejected" className={location.pathname === '/petitions/rejected' ? 'active' : ''}>
            <XCircle size={17} className="icon" /> Rejected Petitions
          </MobileLink>
        </MobileSection>

        <MobileSection>
          <MobileSectionTitle><Gavel size={13} /> Parliament</MobileSectionTitle>
          <MobileLink to="/petitions/debate-scheduled" className={location.pathname === '/petitions/debate-scheduled' ? 'active' : ''}>
            <CalendarDays size={17} className="icon" /> Debate Scheduled
          </MobileLink>
          <MobileLink to="/petitions/debated" className={location.pathname === '/petitions/debated' ? 'active' : ''}>
            <Building2 size={17} className="icon" /> Debated
          </MobileLink>
          <MobileLink to="/petitions/government-responded" className={location.pathname === '/petitions/government-responded' ? 'active' : ''}>
            <MessageSquare size={17} className="icon" /> Gov Responded
          </MobileLink>
          <MobileLink to="/petitions/milestones" className={location.pathname === '/petitions/milestones' ? 'active' : ''}>
            <Target size={17} className="icon" /> Near Milestones
          </MobileLink>
        </MobileSection>

        <MobileSection>
          <MobileSectionTitle><Sparkles size={13} /> Discover</MobileSectionTitle>
          <MobileLink to="/compare" className={location.pathname === '/compare' ? 'active' : ''}>
            <GitCompare size={17} className="icon" /> Compare Petitions
          </MobileLink>
          <MobileLink to="/petitions/trending" className={location.pathname === '/petitions/trending' ? 'active' : ''}>
            <TrendingUp size={17} className="icon" /> Trending Now
          </MobileLink>
          <MobileLink to="/petitions/recently-opened" className={location.pathname === '/petitions/recently-opened' ? 'active' : ''}>
            <Sparkles size={17} className="icon" /> Recently Opened
          </MobileLink>
          <MobileLink to="/petitions/local" className={location.pathname === '/petitions/local' ? 'active' : ''}>
            <MapPin size={17} className="icon" /> Local Petitions
          </MobileLink>
          <MobileLink to="/petitions/recently-closed" className={location.pathname === '/petitions/recently-closed' ? 'active' : ''}>
            <Archive size={17} className="icon" /> Recently Closed
          </MobileLink>
        </MobileSection>

        <MobileSection>
          <MobileSectionTitle><HelpCircle size={13} /> More</MobileSectionTitle>
          <MobileLink to="/faq" className={location.pathname === '/faq' ? 'active' : ''}>
            <HelpCircle size={17} className="icon" /> FAQ
          </MobileLink>
          <MobileLink to="/about" className={location.pathname === '/about' ? 'active' : ''}>
            <Users size={17} className="icon" /> About Us
          </MobileLink>
          <MobileLink to="/contact" className={location.pathname === '/contact' ? 'active' : ''}>
            <Mail size={17} className="icon" /> Contact
          </MobileLink>
          <MobileLinkExternal
            href="https://petition.parliament.uk/petitions/new"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ScrollText size={17} className="icon" /> Create a Petition
          </MobileLinkExternal>
        </MobileSection>

      </MobileNav>
    </HeaderWrapper>
  );
};

export default Header;
